<!-- ══════════════════════════════════════════════════════════════════════════
     MAIN COMPONENT — app shell with Ionic tabs + ion-modal
     ══════════════════════════════════════════════════════════════════════════
     DESIGNER QUICK MAP
     ─────────────────────────────────────────────────────────────────────────
     App background ......... .app-shell           → radial gradient + --ion-background-color
     Top bar ................ <top-bar>             → liquid-glass ion-header
     Tab content ............ ion-tabs              → Ionic router outlet
     Tab bar ................ ion-tab-bar           → liquid-glass floating pill
     No-access modal ........ ion-modal             → liquid-glass, backdropDismiss=false
     Settings modal ......... ion-modal             → --surface-container, custom sizing
     Airline message modal .. ion-modal             → card style
     Flight details modal ... ion-modal             → full screen
     ══════════════════════════════════════════════════════════════════════════ -->

<div class="app-shell">
  <!-- ─── REGION: Top bar ─────────────────────────────────────────────────
       Connectivity indication lives INSIDE the bar (connectivity-status chip,
       OfflineMode-gated) — the former full-width offline banner was absorbed
       into it on 2026-08-18. ─────────────────────────────────────────── -->
  @if (ds.canAccessApp()) {
    <top-bar></top-bar>
  }

  <!-- ─── REGION: Tab content + bottom navigation ─────────────────────
       app-content wrapper gives ion-tabs a positioned + flex-bounded
       containing block; without it ion-tabs's absolute-positioned inner
       router-outlet doesn't track window resize and ion-content can't
       shrink, breaking scroll on short viewports. Mirrors the pwi layout. -->
  <div class="app-content">
    <!-- ─── REGION: Navigation progress ──────────────────────────────────
         Indeterminate sliver pinned to the top edge of the tab content, i.e.
         directly under the top bar. Absolutely positioned so it overlays the
         page instead of reflowing it. Only renders once a navigation has run
         past RootStore's grace period, so instant tab switches never flash it.
         Decorative — the route change itself is what a screen reader follows. -->
    @if (rs.navigating()) {
      <div
        class="nav-progress"
        aria-hidden="true"></div>
    }

    <ion-tabs
      [class.content-hidden]="!ds.canAccessApp()"
      (ionTabsDidChange)="rs.syncView($event.tab)">
      @if (ds.canAccessApp()) {
        <ion-tab-bar
          slot="bottom"
          class="tab-bar-glass"
          tourAnchor="nav.tabs">
          <!-- [class.tab-selected] mirrors the store so the active tab is highlighted
               on launch (the '' → /home redirect completes before ion-tab-bar renders,
               so Ionic's URL-driven selection misses the initial navigation). -->
          <ion-tab-button
            tab="home"
            tourAnchor="nav.home"
            [class.tab-selected]="rs.currentView() === appViews.home">
            <fa-icon
              class="tab-icon"
              [icon]="['fas', 'house']"></fa-icon>
            <span class="tab-label">Home</span>
          </ion-tab-button>

          <ion-tab-button
            tab="flights"
            tourAnchor="nav.flights"
            [class.tab-selected]="rs.currentView() === appViews.flights">
            <fa-icon
              class="tab-icon"
              [icon]="['fas', 'plane']"></fa-icon>
            <span class="tab-label">Flights</span>
          </ion-tab-button>

          <ion-tab-button
            tab="briefing"
            tourAnchor="nav.briefing"
            [class.tab-selected]="rs.currentView() === appViews.briefing">
            <fa-icon
              class="tab-icon"
              [icon]="['fas', 'file-lines']"></fa-icon>
            <span class="tab-label">Briefing</span>
          </ion-tab-button>

          <ion-tab-button
            tab="reports"
            tourAnchor="nav.reports"
            [class.tab-selected]="rs.currentView() === appViews.reports">
            <fa-icon
              class="tab-icon"
              [icon]="['fas', 'chart-bar']"></fa-icon>
            <span class="tab-label">Reports</span>
          </ion-tab-button>
        </ion-tab-bar>
      }
    </ion-tabs>
  </div>

  <!-- ─── Flight details overlay — sits below the top bar, above tab content.
       Not rendered while the flights page embeds the open flight itself (split
       view, FlightsSplitView flag): the store decides which of the two shows. ── -->
  @if (fs.detailOverlayShowing()) {
    <div class="flight-details-overlay">
      <flight-details></flight-details>
    </div>
  }

  <!-- ─── Impersonation indicator — floating yellow rectangle, bottom-left ──
       position: fixed inside the component, so it never affects any layout and
       floats above every view (incl. the flight-details overlay). Self-renders
       only while an impersonation override is active. -->
  <impersonation-banner></impersonation-banner>
</div>

<!-- ─── MODAL: Airline message (card style) ──────────────────────────────── -->
<ion-modal
  [isOpen]="rs.showAirlineMessage()"
  (ionModalDidDismiss)="rs.displayAirlineMessage(false)"
  cssClass="airline-message-modal">
  <ng-template>
    <ion-header class="ion-no-border">
      <!-- Shared modal-header pattern (same as Flight Ops / METAR dialogs):
           plain DOM, so the title reads in the normal dark content colour
           instead of ion-toolbar's unreadable light-on-light theming. -->
      <div class="pwi-modal-header flex-row items-center">
        <span class="main-title">Airline Message</span>
        <span class="spacer"></span>
        <button
          class="close-btn-round"
          aria-label="Close"
          (click)="rs.displayAirlineMessage(false)">
          <ion-icon name="close"></ion-icon>
        </button>
      </div>
    </ion-header>
    <ion-content class="ion-padding">
      @if (rs.airlineMessageContentLoading()) {
        <div class="flex-col flex-center">
          <ion-spinner name="crescent"></ion-spinner>
        </div>
      } @else {
        <div
          [innerHTML]="rs.airlineMessageContent() | safeHtml"
          [saraReadableOnDark]="rs.isDarkTheme()"></div>
      }
    </ion-content>
  </ng-template>
</ion-modal>

<!-- ─── MODAL: No-access (blocks entire screen) ───────────────────────────
     Fixed width, height grows with content (capped by --max-height on the
     ion-modal so the disclaimer scrolls). Uses the app's mat-flat-button
     + btn-confirm/btn-warning/btn-neutral pattern instead of ion-button so
     actions match the rest of the app. -->
<ion-modal
  [isOpen]="!ds.canAccessApp() && (ds.noAccessType() !== null || !ds.bootCompleted())"
  [backdropDismiss]="false"
  cssClass="no-access-modal">
  <ng-template>
    <div class="no-access-shell">
      @switch (ds.noAccessType()) {
        @case (noAccessTypes.NotAcknowledged) {
          <header class="no-access-header">
            <h2 class="no-access-title">Confidentiality Clause</h2>
          </header>
          <div class="no-access-body">
            <div [innerHTML]="ds.pwiDisclaimer() | safeHtml"></div>
            @if (ds.isImpersonating()) {
              <!-- Read-only impersonation: the clause is per-crew and persisted
                   server-side, so an impersonating viewer cannot acknowledge it on
                   the pilot's behalf. Show it for reference; the Acknowledge write
                   is swapped for a "Continue (read-only)" escape in the footer. -->
              <p class="text-center">
                You're viewing this in read-only impersonation — the confidentiality clause can't be acknowledged on the
                pilot's behalf. Continue to view the app read-only.
              </p>
            }
          </div>
          <footer class="no-access-footer">
            <button
              mat-flat-button
              class="btn-warning"
              (click)="ds.logout()">
              Reject
            </button>
            @if (ds.isImpersonating()) {
              <!-- Read-only escape: enter the app without acknowledging on the
                   pilot's behalf (client-side, no backend write). -->
              <button
                mat-flat-button
                class="btn-confirm"
                (click)="ds.continueImpersonationReadonly()">
                Continue (read-only)
              </button>
            } @else {
              <button
                mat-flat-button
                class="btn-confirm"
                (click)="ds.acknowledgeDisclaimer()">
                Acknowledge
              </button>
            }
          </footer>
        }

        @case (noAccessTypes.SessionLoadFailed) {
          <header class="no-access-header">
            <h2 class="no-access-title">Couldn't Load Your Session</h2>
          </header>
          <div class="no-access-body text-center">
            <p>We couldn't finish loading your session. Please try again.</p>
          </div>
          <footer class="no-access-footer">
            <button
              mat-flat-button
              class="btn-neutral"
              (click)="ds.logout()">
              Logout
            </button>
            <button
              mat-flat-button
              class="btn-confirm"
              (click)="ds.retrySessionLoad()">
              Retry
            </button>
          </footer>
        }

        @case (noAccessTypes.DisclaimerFetchFailed) {
          <header class="no-access-header">
            <h2 class="no-access-title">Confidentiality Clause Unavailable</h2>
          </header>
          <div class="no-access-body text-center">
            <p>
              We couldn't load the confidentiality clause. You'll be able to review and acknowledge it once it loads —
              please try again.
            </p>
          </div>
          <footer class="no-access-footer">
            <button
              mat-flat-button
              class="btn-neutral"
              (click)="ds.logout()">
              Logout
            </button>
            <button
              mat-flat-button
              class="btn-confirm"
              (click)="ds.retryDisclaimer()">
              Retry
            </button>
          </footer>
        }

        @case (noAccessTypes.ChooseImpersonation) {
          <header class="no-access-header">
            <h2 class="no-access-title">Choose a pilot</h2>
            <p class="no-access-subtitle">You'll browse PWI read-only as the pilot you select.</p>
          </header>
          <div class="no-access-body">
            <div class="impersonation-target-list">
              @for (target of ds.impersonationTargets() ?? []; track target.code) {
                <button
                  type="button"
                  class="impersonation-target"
                  (click)="ds.applyImpersonationChoice(target.code)">
                  <span
                    class="impersonation-target-avatar"
                    aria-hidden="true">
                    {{ target.code.charAt(0) }}
                  </span>
                  <span class="impersonation-target-main">
                    <span class="impersonation-target-code">{{ target.code }}</span>
                    @if (target.fleets.length) {
                      <span class="impersonation-target-fleets">
                        @for (fleet of target.fleets; track fleet.id) {
                          <span class="impersonation-target-fleet">{{ fleet.name }}</span>
                        }
                      </span>
                    }
                  </span>
                  <ion-icon
                    class="impersonation-target-chevron"
                    name="chevron-forward"
                    aria-hidden="true"></ion-icon>
                </button>
              }
            </div>
          </div>
          <footer class="no-access-footer">
            <button
              mat-flat-button
              class="btn-neutral"
              (click)="ds.logout()">
              Logout
            </button>
          </footer>
        }

        @case (noAccessTypes.NoFleet) {
          <header class="no-access-header">
            <h2 class="no-access-title">No Fleet Assigned Yet</h2>
          </header>
          <div class="no-access-body text-center">
            <div [innerHTML]="ds.noAccessMessage() | safeHtml"></div>
          </div>
        }

        @case (noAccessTypes.UserFetchFailed) {
          <header class="no-access-header">
            <h2 class="no-access-title">Service Unavailable</h2>
          </header>
          <div class="no-access-body text-center">
            <div [innerHTML]="ds.noAccessMessage() | safeHtml"></div>
          </div>
          <footer class="no-access-footer">
            <button
              mat-flat-button
              class="btn-neutral"
              (click)="ds.logout()">
              Logout
            </button>
            <button
              mat-flat-button
              class="btn-confirm"
              (click)="ds.retryUserFetch()">
              Retry
            </button>
          </footer>
        }

        @case (noAccessTypes.TokenExpired)
        @case (noAccessTypes.UserIdle) {
          <header class="no-access-header">
            <h2 class="no-access-title">
              {{ ds.noAccessType() === noAccessTypes.TokenExpired ? 'Session Expired' : 'Session Idle' }}
            </h2>
          </header>
          <div class="no-access-body text-center">
            <p>{{ ds.noAccessMessage() }} — Please login again</p>
          </div>
          <footer class="no-access-footer">
            <button
              mat-flat-button
              class="btn-confirm"
              (click)="ds.login()">
              Login to access SARA PWI
            </button>
          </footer>
        }

        @default {
          <!-- Initial-boot "Loading your session..." spinner. This arm is only
               reachable while booting: the modal's [isOpen] now requires either
               a matched no-access case OR !bootCompleted, so a post-boot
               canAccessApp=false flip with no matched case (a transient on tab
               resume / screen-lock) never opens the modal — no more empty card. -->
          <div class="loading-state flex-col flex-center">
            <img
              class="loading-logo"
              src="assets/logos/sarapwi-dark-200px-72ppi.png"
              alt="Sara PWI" />
            <ion-spinner name="crescent"></ion-spinner>
            <p class="loading-text">Loading your session...</p>
            <!-- Manual escape hatch: appears once boot has dragged past
                 BOOT_REFRESH_DELAY_MS so the user is never trapped on a bare
                 spinner. Hard reload (reloadApp) recovers a wedged client. -->
            @if (bootRefreshVisible()) {
              <div class="loading-refresh flex-col flex-center">
                <p class="loading-subtext">This is taking longer than expected.</p>
                <button
                  mat-flat-button
                  class="btn-confirm"
                  (click)="reloadApp()">
                  Refresh
                </button>
              </div>
            }
          </div>
        }
      }
    </div>
  </ng-template>
</ion-modal>

<!-- ─── MODAL: User settings — 2-column sidebar layout ───────────────────── -->
<ion-modal
  [isOpen]="rs.showSettings()"
  (ionModalDidDismiss)="rs.displaySettings(false)"
  cssClass="settings-modal">
  <ng-template>
    <div class="settings-layout">
      <!-- ── LEFT SIDEBAR ──────────────────────────────────────── -->
      <div class="settings-sidebar">
        <div class="sidebar-profile">
          <div class="profile-avatar">{{ ds.userInitials() }}</div>
          <div class="profile-info">
            <span class="profile-name">{{ ds.userName() }}</span>
            <span class="profile-role">Pilot</span>
          </div>
        </div>

        <nav class="sidebar-nav">
          @for (item of rs.settingsNav(); track item.id) {
            <button
              class="settings-nav-item"
              [class.active]="rs.activeSettingsSection() === item.id"
              (click)="rs.setActiveSettingsSection(item.id)">
              <sara-icon
                class="icon-s"
                [icon]="item.icon"
                variant="solid"></sara-icon>
              <span>{{ item.label }}</span>
            </button>
          }
        </nav>

        <div class="sidebar-footer">
          <button
            class="logout-btn"
            (click)="ds.logout()">
            <ion-icon name="log-out-outline"></ion-icon>
            Logout
          </button>
        </div>
      </div>

      <!-- ── RIGHT CONTENT ──────────────────────────────────────── -->
      <div class="settings-content">
        <div class="content-header">
          <span class="content-title">{{ rs.activeSettingsSectionLabel() }}</span>
          <button
            class="close-btn-round"
            aria-label="Close"
            (click)="rs.displaySettings(false)">
            <ion-icon name="close"></ion-icon>
          </button>
        </div>

        <div class="content-body">
          @switch (rs.activeSettingsSection()) {
            @case ('appearance') {
              <div class="settings-row">
                <span class="settings-label">Theme</span>
                <div class="theme-toggle">
                  <button
                    class="theme-opt"
                    [class.active]="rs.currentTheme() === appTheme.light"
                    (click)="rs.setTheme(appTheme.light)">
                    <sara-icon
                      icon="sun"
                      variant="solid"></sara-icon>
                    Light
                  </button>
                  <button
                    class="theme-opt"
                    [class.active]="rs.currentTheme() === appTheme.dark"
                    (click)="rs.setTheme(appTheme.dark)">
                    <sara-icon
                      icon="moon"
                      variant="solid"></sara-icon>
                    Dark
                  </button>
                </div>
              </div>
            }

            @case ('notifications') {
              <!-- Every control on this screen reads and writes ONE endpoint,
                   /user-config/notifications, through ds.channelPrefs. The
                   summary and the Flights-availability Email used to be read
                   from the legacy /user-config/mail instead, so the screen
                   posted the truth to one endpoint and displayed another; both
                   write the same CrewConfig row, which is why it never showed.
                   Behind the PilotNotifications flag until the PwiApi
                   notifications route is live in production — see
                   `notificationsDisabled` in main.component.ts. Flip it from
                   Settings → Developer to exercise the screen.
                   With the flag off the nav omits this section entirely (see
                   RootStore.settingsNav), so there is no "not available yet"
                   hint any more — an unfinished feature should not advertise
                   itself. This gate is the second lock, for a future deep-link
                   that could reach the pane without going through the nav; it
                   also means nothing below needs to re-test the flag. -->
              @if (!notificationsDisabled()) {
                @if (ds.fleetNotificationPolicy().hasAdminLock) {
                  <!-- Fleet config turned channels off (or allowChange=false):
                     the locked toggles below are dead server-side anyway. -->
                  <div class="settings-empty-hint">Some notifications are disabled by your administrator.</div>
                }
                <div class="settings-row settings-row--group-head">
                  <span class="settings-label">Flights availability</span>
                </div>
                <div class="settings-row settings-row--child">
                  <span class="settings-label settings-label--sub">
                    <sara-icon
                      icon="mobile-screen"
                      variant="solid"></sara-icon>
                    Push
                  </span>
                  <ion-toggle
                    [disabled]="
                      ds.isImpersonating() ||
                      !ds.fleetNotificationPolicy().allowChange ||
                      !ds.fleetNotificationPolicy().flightPush
                    "
                    [checked]="ds.channelPrefs().flightAvailabilityPush"
                    (ionChange)="
                      ds.setNotificationChannel('flightAvailabilityPush', $any($event).detail.checked)
                    "></ion-toggle>
                </div>
                <div class="settings-row settings-row--child">
                  <span class="settings-label settings-label--sub">
                    <sara-icon
                      icon="envelope"
                      variant="regular"></sara-icon>
                    Email
                  </span>
                  <ion-toggle
                    [disabled]="
                      !ds.channelPrefs().summaryEnabled ||
                      ds.isImpersonating() ||
                      !ds.fleetNotificationPolicy().allowChange ||
                      !ds.fleetNotificationPolicy().flightEmail
                    "
                    [checked]="ds.channelPrefs().flightAvailabilityEmail"
                    (ionChange)="
                      ds.setNotificationChannel('flightAvailabilityEmail', $any($event).detail.checked)
                    "></ion-toggle>
                </div>

                <div class="settings-row settings-row--group-head">
                  <span class="settings-label">New comment</span>
                </div>
                <div class="settings-row settings-row--child">
                  <span class="settings-label settings-label--sub">
                    <sara-icon
                      icon="mobile-screen"
                      variant="solid"></sara-icon>
                    Push
                  </span>
                  <ion-toggle
                    [disabled]="
                      ds.isImpersonating() ||
                      !ds.fleetNotificationPolicy().allowChange ||
                      !ds.fleetNotificationPolicy().commentPush
                    "
                    [checked]="ds.channelPrefs().newCommentPush"
                    (ionChange)="ds.setNotificationChannel('newCommentPush', $any($event).detail.checked)"></ion-toggle>
                </div>
                <div class="settings-row settings-row--child">
                  <span class="settings-label settings-label--sub">
                    <sara-icon
                      icon="envelope"
                      variant="regular"></sara-icon>
                    Email
                  </span>
                  <ion-toggle
                    [disabled]="
                      ds.isImpersonating() ||
                      !ds.fleetNotificationPolicy().allowChange ||
                      !ds.fleetNotificationPolicy().commentEmail
                    "
                    [checked]="ds.channelPrefs().newCommentEmail"
                    (ionChange)="
                      ds.setNotificationChannel('newCommentEmail', $any($event).detail.checked)
                    "></ion-toggle>
                </div>

                <div class="settings-row settings-row--group-head">
                  <span class="settings-label">Receive summary email</span>
                  <ion-toggle
                    [disabled]="
                      ds.isImpersonating() ||
                      !ds.fleetNotificationPolicy().allowChange ||
                      !ds.fleetNotificationPolicy().summaryEmail
                    "
                    [checked]="ds.channelPrefs().summaryEnabled"
                    (ionChange)="ds.setSummarySubscription($any($event).detail.checked)"></ion-toggle>
                </div>
                <div class="settings-row settings-row--child">
                  <span class="settings-label settings-label--sub">
                    <sara-icon
                      icon="clock"
                      variant="regular"></sara-icon>
                    Frequency
                  </span>
                  <button
                    class="select-btn"
                    [disabled]="
                      !ds.channelPrefs().summaryEnabled ||
                      ds.isImpersonating() ||
                      !ds.fleetNotificationPolicy().allowChange ||
                      !ds.fleetNotificationPolicy().summaryEmail
                    "
                    [matMenuTriggerFor]="freqMenu">
                    {{ ds.summaryDelayString() }}
                    <ion-icon name="chevron-down"></ion-icon>
                  </button>
                  <!-- Weekly and Monthly only: Daily was dropped from the offer.
                     The current cadence is marked by DISABLING its own entry,
                     so a pilot cannot re-pick what they already have.
                     Legacy cadences (1 = Daily, 6 = the Weekly we used to
                     write) still read back with their label on the button, but
                     they match neither entry, so both stay clickable and
                     picking one normalises the pilot onto 7 or 30. That is the
                     intent — there is no way back to a retired cadence.
                     See getDelayString in withUserSettings.ts. -->
                  <mat-menu #freqMenu="matMenu">
                    <button
                      mat-menu-item
                      (click)="ds.setSummaryCadence(7)"
                      [disabled]="ds.channelPrefs().summaryDayDelay === 7">
                      Weekly
                    </button>
                    <button
                      mat-menu-item
                      (click)="ds.setSummaryCadence(30)"
                      [disabled]="ds.channelPrefs().summaryDayDelay === 30">
                      Monthly
                    </button>
                  </mat-menu>
                </div>
              }
            }

            @case ('dashboard') {
              <!-- "Show comparisons" gates the trend deltas on the home Overview
                   tiles; the per-widget toggles (one group per category) drive
                   which tiles that card renders. Both via rs, persisted locally. -->
              <div class="settings-row">
                <span class="settings-label">Show comparisons</span>
                <ion-toggle
                  [checked]="rs.showComparisons()"
                  (ionChange)="rs.setShowComparisons($any($event).detail.checked)"></ion-toggle>
              </div>

              @for (group of rs.dashboardWidgetGroups(); track group.key) {
                <div class="settings-section-title">{{ group.label }}</div>
                @for (item of group.items; track item.id) {
                  <div class="settings-row">
                    <div class="stat-toggle-info">
                      <span class="stat-toggle-icon">
                        <sara-icon
                          [icon]="item.iconName"
                          variant="solid"></sara-icon>
                      </span>
                      <span class="settings-label">{{ item.label }}</span>
                    </div>
                    @if (item.locked) {
                      <!-- The tap lands here (a disabled ion-toggle is
                           pointer-events: none), on `pointerup` so a scroll
                           starting on the toggle — cancelled, never released —
                           neither shakes nor buzzes. -->
                      <div
                        class="locked-toggle"
                        [class.shaking]="shakenWidget() === group.key + ':' + item.id"
                        title="Required by your airline"
                        (pointerup)="shakeLockedWidget(group.key, item.id)">
                        <ion-toggle
                          [checked]="item.enabled"
                          [disabled]="true"></ion-toggle>
                        <sara-icon
                          class="locked-toggle-badge"
                          icon="lock"
                          variant="solid"></sara-icon>
                      </div>
                    } @else {
                      <ion-toggle
                        [checked]="item.enabled"
                        (ionChange)="rs.setWidgetVisible(group.key, item.id, $any($event).detail.checked)"></ion-toggle>
                    }
                  </div>
                }
              } @empty {
                <!-- Fleet has no config-v2 widget shortlist — gated groups are
                     all empty, so there's nothing to toggle. -->
                <div class="settings-empty-hint">No overview widgets are enabled for this fleet.</div>
              }
            }

            @case ('help') {
              <!-- User manual — served from app assets (PDF, generated from
                   MAN-05). `download` saves on desktop browsers; on the iOS
                   webview the link opens the system PDF viewer instead (share/
                   save from there) — target=_blank keeps the app alive. -->
              <div class="settings-row">
                <div class="stat-toggle-info">
                  <span class="stat-toggle-icon">
                    <sara-icon
                      icon="file-pdf"
                      variant="solid"></sara-icon>
                  </span>
                  <span class="settings-label">User manual</span>
                </div>
                <a
                  class="select-btn"
                  href="assets/docs/sara-pwi-user-manual.pdf"
                  download="SARA-PWI-User-Manual.pdf"
                  target="_blank"
                  rel="noopener">
                  Download
                </a>
              </div>

              <!-- Guided tour (LBO's guide settings): replay the FULL
                   app-wide tour, and the hide-"?" preference — tours stay
                   reachable from here once hidden. Un-parked at rollout: the
                   tours ship on, and this button is the only replay of the
                   whole storyline once it auto-played (the top-bar "?" covers
                   single pages). The explicit Home redirect (closeFlight drops
                   a stale ?id=) guarantees the walk starts at its first step
                   even where the drive effect won't fire — restarting while
                   the app tour already sits on a 'home' step is memoized. -->
              <div class="settings-row">
                <div>
                  <span class="settings-label">Guided tour</span>
                  <div class="settings-hint">
                    Replays the complete tour of the app, page by page. For a single page's tour, use the "?" button in
                    its top bar.
                  </div>
                </div>
                <button
                  class="select-btn"
                  (click)="
                    rs.displaySettings(false); rs.navigateTo(appViews.home, true); toursStore.restartTour('app')
                  ">
                  Start the tour
                </button>
              </div>
              <div class="settings-row">
                <div>
                  <span class="settings-label">Hide the "?" help button</span>
                  <div class="settings-hint">
                    Removes the circular "?" pill from the top bar of every page. You can still restart the tour from
                    here at any time.
                  </div>
                </div>
                <ion-toggle
                  [checked]="toursStore.helpButtonHidden()"
                  (ionChange)="toursStore.setHelpButtonHidden($any($event).detail.checked)"></ion-toggle>
              </div>
            }

            @case ('developer') {
              <!-- Dev-only feature-flag overrides (local, persisted). Mirrors
                   JMO's SARASettings debug panel, adapted to the settings-row
                   style. The whole section is gated on isDev via rs.settingsNav(),
                   so pilots never see it. A toggle writes a local override (dev
                   only); Reset clears all overrides so flags follow the (mocked)
                   server values again. NB: OfflineMode is reserved for the planned
                   offline mode — toggling it has no visible effect until that
                   feature is built and gated on isEnabled('OfflineMode'). -->
              <div class="settings-row settings-row--group-head">
                <span class="settings-label">Feature flags</span>
                <button
                  class="select-btn"
                  (click)="ffs.resetOverrides()">
                  Reset overrides
                </button>
              </div>

              @if (ffs.serverFlags().error; as loadError) {
                <div class="settings-row settings-row--child">
                  <span class="settings-label settings-label--sub">Couldn't load flags ({{ loadError }})</span>
                  <button
                    class="select-btn"
                    (click)="ffs.loadFlags()">
                    Retry
                  </button>
                </div>
              }

              @for (flag of featureFlagRows(); track flag.name) {
                <div class="settings-row settings-row--child">
                  <span class="settings-label settings-label--sub">
                    {{ flag.name }}
                    @if (flag.overridden) {
                      <em>· local override</em>
                    }
                  </span>
                  <ion-toggle
                    [checked]="flag.effective"
                    (ionChange)="ffs.setOverride(flag.name, $any($event).detail.checked)"></ion-toggle>
                </div>
              }

              <!-- UI overrides — dev-only state forcing, same mechanics as the
                   flag overrides (local, persisted, inert on pilot builds). Real
                   data rarely shows every state a card can take; these force one
                   on the app's own data so a state-bound visual can be reproduced
                   and checked. The top bar shows a reminder while any is on. -->
              <div class="settings-row settings-row--group-head">
                <span class="settings-label">UI overrides</span>
                <button
                  class="select-btn"
                  (click)="uio.resetOverrides()">
                  Reset overrides
                </button>
              </div>
              <div class="settings-row settings-row--child">
                <span class="settings-label settings-label--sub">Flight cards: not opened yet</span>
                <ion-toggle
                  [checked]="uio.overrides().flightsUnopened"
                  (ionChange)="uio.setOverride('flightsUnopened', $any($event).detail.checked)"></ion-toggle>
              </div>
              <div class="settings-row settings-row--child">
                <span class="settings-label settings-label--sub">Flight cards: unread messages</span>
                <ion-toggle
                  [checked]="uio.overrides().flightsUnread"
                  (ionChange)="uio.setOverride('flightsUnread', $any($event).detail.checked)"></ion-toggle>
              </div>
              <div class="settings-row settings-row--child">
                <span class="settings-label settings-label--sub">Flight cards: header tint</span>
                <div class="pwi-pill pwi-segmented pwi-segmented--solid">
                  @for (tint of flightTintOptions; track tint.label) {
                    <button
                      type="button"
                      class="pwi-segmented-btn"
                      [class.pwi-segmented-btn--active]="uio.overrides().flightTint === tint.value"
                      (click)="uio.setOverride('flightTint', tint.value)">
                      {{ tint.label }}
                    </button>
                  }
                </div>
              </div>
              <div class="settings-empty-hint">
                A forced tint is a forced state: a "No data" card also opens as one. Applies to the flights list, the
                home "Flights to review" and the reports drawer.
              </div>

              <!-- Tour studio — dev-only authoring panel for the guided tours
                   (compose a TourDef from the current page's anchors, preview
                   through the real player, export JSON). Closes the settings
                   modal so the page under authoring is visible.
                   The GuidedTours flag now gates the STUDIO only: the tours
                   themselves ship on, the authoring tool stays behind it. -->
              @if (ffs.isEnabled('GuidedTours')) {
                <div class="settings-row settings-row--group-head">
                  <span class="settings-label">Tour studio</span>
                  <button
                    class="select-btn"
                    (click)="tourStudio.openStudio(); rs.displaySettings(false)">
                    Open
                  </button>
                </div>
              }

              <!-- Push notifications — dev-only full-chain check: PwiApi sends
                   a test push to this crew's registered devices (app → PwiApi →
                   Notification Hub → APNs → iPad), bypassing trigger/gating. -->
              <div class="settings-row settings-row--group-head">
                <span class="settings-label">Test push notification</span>
                <button
                  class="select-btn"
                  (click)="sendTestPush()">
                  Send
                </button>
              </div>

              <!-- Guided tours backend — dev-only seeding + diagnostic. The
                   content-source row answers "backend or local fallback?"
                   (deliberately invisible in the UI otherwise). The Seed button
                   PUTs the BUILT-IN catalog (LOCAL_TOURS — NOT the studio
                   draft) via the admin endpoint, then reports what GET /tours
                   actually serves (the unwired-_CONFIG case accepts writes
                   with 200 and drops them — only the verify read exposes it).
                   Requires the admin role, rejected while impersonating. -->
              <div class="settings-row settings-row--group-head">
                <span class="settings-label">Guided tours backend</span>
                <button
                  class="select-btn"
                  (click)="publishTours()">
                  Seed built-in tours
                </button>
              </div>
              <div class="settings-row settings-row--child">
                <span class="settings-label settings-label--sub">Content source</span>
                <span class="settings-label settings-label--sub">{{ toursSource.contentSource() }}</span>
              </div>
              <div class="settings-row settings-row--child">
                <span class="settings-label settings-label--sub">Engine version</span>
                <span class="settings-label settings-label--sub">{{ tourEngineVersion }}</span>
              </div>
              <div class="settings-empty-hint">
                Seeding publishes the app's built-in catalog — studio drafts aren't included (no backend save yet).
              </div>
            }
          }
        </div>
      </div>
    </div>
  </ng-template>
</ion-modal>

<!-- ─── REGION: Guided-tour overlay host ────────────────────────────────
     One tag renders everything a running tour needs (spotlight + step card
     as a CDK overlay, live repositioning) — @ergoss/guided-tour. -->
<tour-host />

<!-- ─── REGION: Tour studio (dev tool) ──────────────────────────────────
     Deferred: the studio chunk only loads the first time it's opened (from
     Settings → Developer), so the authoring tool never weighs on the pilot
     bundle. The inner @if unmounts the panel on close. -->
@defer (when tourStudio.open()) {
  @if (tourStudio.open()) {
    <!-- saveable: the admin PUT behind saveStudioTour rejects impersonated
         sessions, so the Save button hides rather than dead-ends there. -->
    <tour-studio
      [saveable]="!ds.isImpersonating()"
      (save)="saveStudioTour($event)"
      (notify)="studioNotify($event)" />
  }
}
